/**
 * Display helpers for ALP package flight legs. Provider unix values are local
 * wall-clock encoded as UTC (same convention as FlightBox / the BE
 * ResultFormatter display fields) — render with getUTC* so text === BE message.
 */
const pad = (n: number) => String(n).padStart(2, '0');

/** "DD/MM HH:mm" (UTC) from unix seconds. */
export function formatPackageFlightDateTime(unixSeconds?: number | string | null): string | null {
  const n = Number(unixSeconds);
  if (!Number.isFinite(n) || n <= 0) return null;
  const d = new Date(n * 1000);
  return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

/** "HH:mm" (UTC) from unix seconds. */
export function formatPackageFlightTime(unixSeconds?: number | string | null): string | null {
  const n = Number(unixSeconds);
  if (!Number.isFinite(n) || n <= 0) return null;
  const d = new Date(n * 1000);
  return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

/** "DD/MM HH:mm → HH:mm" range for one leg. */
export function formatPackageFlightRange(
  departsUnix?: number | string | null,
  arrivesUnix?: number | string | null,
): string | null {
  const dep = formatPackageFlightDateTime(departsUnix);
  if (!dep) return null;
  const arr = formatPackageFlightTime(arrivesUnix);
  return arr ? `${dep} → ${arr}` : dep;
}

export interface PackageFlightLeg {
  airline?: { id?: string; name?: string; image?: string } | string | null;
  flight_number?: string;
  from?: { code?: string; name?: string } | string;
  to?: { code?: string; name?: string } | string;
  departure_date?: number;
  arrival_date?: number;
  duration?: string;
  baggage?: {
    is_include_suitcase?: boolean;
    is_include_trolly?: boolean;
    handbag_weight?: string | number;
  };
}

export function packageLegAirlineName(leg?: PackageFlightLeg | null): string | undefined {
  const a = leg?.airline;
  if (!a) return undefined;
  return typeof a === 'string' ? a : a.name ?? a.id;
}

export function packageLegAirlineImage(leg?: PackageFlightLeg | null): string | undefined {
  const a = leg?.airline;
  return typeof a === 'object' && a ? a.image : undefined;
}

export function packageLegRoute(leg?: PackageFlightLeg | null): string | undefined {
  if (!leg) return undefined;
  const code = (v: any) => (typeof v === 'string' ? v : v?.code ?? v?.name);
  const from = code(leg.from);
  const to = code(leg.to);
  return from && to ? `${from} → ${to}` : undefined;
}
